import React, {useEffect, useState} from "react";
import { TextField } from "@mui/material";
import { ArrowBackIosNew } from "@mui/icons-material";
import { GridSaveAltIcon } from "@mui/x-data-grid";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button, Modal, Grid, Stack, Typography, ModalClose, Sheet } from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IWorkScope from "../../../types/work-scope.type";
import ITechnique from "../../../types/technique.type";
import {Autocomplete} from "@mui/lab";
import IUser from "../../../types/user.type";

interface ModalProps {
  isOpen: boolean,
  onSubmit: (data: IWorkScope) => void,
  onClose: () => void,
  workScope: IWorkScope | undefined,
  techniques: ITechnique[],
  users: IUser[],
  readOnly?: boolean;
}

const WorkScopeModal: React.FC<ModalProps> = (props) => {
  const { isOpen, onClose, onSubmit, workScope, techniques, users, readOnly = false} = props;
  const [resources, setResources] = useState<Array<IUser>>([]);
  const [selectedTechniques, setSelectedTechniques] = useState<Array<ITechnique>>([]);

  const initialValues: IWorkScope = {
    id: workScope?.id,
    comments: "",
    days: 0,
    equipment: "",
    location: "",
    resources: [],
    task: "",
    techniques: [],
  };

  const validationSchema = Yup.object({
    equipment: Yup.string().required('El Equipo es requerido'),
    location: Yup.string().required('La Ubicacion es requerida'),
    task: Yup.string().required('La Tarea es requerida'),
    resources: Yup.array().min(1, 'Al menos tiene que seleccionar un (1) recurso').required(),
    techniques: Yup.array().min(1, 'Al menos tiene que seleccionar una (1) tecnica').required(),
    days: Yup.number().min(0).required('Las Jornadas son requeridas'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
  });

  useEffect(() => {
    if (isOpen && workScope?.id) {
      formik.setValues(workScope);
      setResources(workScope.resources);
      setSelectedTechniques(workScope.techniques);
    } else {
      formik.resetForm();
      setResources([]);
      setSelectedTechniques([]);
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Sheet
        variant="outlined"
        sx={{
          width: 730,
          borderRadius: 'md',
          p: 3,
          boxShadow: 'lg',
          maxHeight: 740,
          overflow: "scroll"
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} />
        <Typography level="h4" component="h1" sx={{ paddingLeft: 1 }}>
          {workScope?.id ? "Editar Alcance de Trabajo" : "Nuevo Alcance de Trabajo"}
        </Typography>
        <form className="form" onSubmit={formik.handleSubmit}>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="equipment"
                  label="Equipo"
                  name="equipment"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.equipment}
                  error={!!(formik.touched.equipment && formik.errors.equipment)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                />
                {formik.touched.equipment && formik.errors.equipment ? (
                  <span className="Error-alert">{formik.errors.equipment}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="location"
                  label="Ubicacion"
                  name="location"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.location}
                  error={!!(formik.touched.location && formik.errors.location)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                />
                {formik.touched.location && formik.errors.location ? (
                  <span className="Error-alert">{formik.errors.location}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="task"
                  label="Tarea"
                  name="task"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.task}
                  error={!!(formik.touched.task && formik.errors.task)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                  multiline
                  rows={3}
                />
                {formik.touched.task && formik.errors.task ? (
                  <span className="Error-alert">{formik.errors.task}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <Autocomplete
                  id="techniques"
                  value={selectedTechniques}
                  getOptionLabel={(tecnnique) => tecnnique.name}
                  options={techniques}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id;
                  }}
                  onChange={(e, value) => {
                    setSelectedTechniques(value)
                    formik.setFieldValue('techniques', value ? value : null);
                  }}
                  multiple
                  fullWidth
                  readOnly={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant={"filled"}
                      label="Tecnica"
                      name="techniques"
                      slotProps={{ inputLabel: {shrink: true}}}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={!!(formik.touched.techniques && formik.errors.techniques)}
                    />
                  )}
                />
                {formik.touched.techniques && formik.errors.techniques ? (
                  <span className="Error-alert">{String(formik.errors.techniques)}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="days"
                  label="Jorandas"
                  name="days"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.days}
                  error={!!(formik.touched.days && formik.errors.days)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                />
                {formik.touched.days && formik.errors.days ? (
                  <span className="Error-alert">{formik.errors.days}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <Autocomplete
                  id="resources"
                  disabled={users.length === 0}
                  getOptionLabel={(user) =>
                    `${user?.lastName || ''}, ${user?.name || ''}`
                  }
                  options={users}
                  value={resources}
                  onChange={(e, value) => {
                    setResources(value);
                    formik.setFieldValue("resources", value);
                  }}
                  multiple
                  fullWidth
                  size="small"
                  readOnly={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant={"filled"}
                      label="Recursos"
                      name="resources"
                      slotProps={{ inputLabel: {shrink: true}}}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={!!(formik.touched.resources && formik.errors.resources)}
                    />
                  )}
                />
                {formik.touched.resources && formik.errors.resources ? (
                  <span className="Error-alert">{String(formik.errors.resources)}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="comments"
                  label="Comentarios"
                  name="comments"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.comments}
                  error={!!(formik.touched.comments && formik.errors.comments)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                  multiline
                  rows={3}
                />
                {formik.touched.comments && formik.errors.comments ? (
                  <span className="Error-alert">{formik.errors.comments}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

          </Grid>
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{paddingRight: 1}}>
            <Button variant="outlined" endDecorator={<ArrowBackIosNew />} onClick={onClose}>Cerrar</Button>
            {!readOnly &&
              <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
            }
          </Stack>
        </form>
      </Sheet>
    </Modal>
  );
};

export default WorkScopeModal;
